import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { useToast } from "@/components/feedback";
import { invalidateBalanceQueries } from "@/features/balance/utils/invalidate-balance-queries";

export const useBalanceApprove = (
  toast: ReturnType<typeof useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (_balanceIds: number[]) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      throw new Error(
        "Collecting selected balances is no longer available on this API.",
      );
    },
    onSuccess: async () => {
      await invalidateBalanceQueries(queryClient);
      toast({
        title: "Balances collected",
        description: "Selected balances were collected successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to collect balances. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isSuccess, isError };
};
